/**
 * Staff Calendar View (Feb 27 2026 — Truck Calendar Phase 1B).
 *
 * Public route at `/staff/:token`. No login required — the URL itself
 * is the credential, minted by `POST /api/owner/staff`. The component
 * renders a read-only chronological list of every event the owner
 * marked `public` or `staff` for the next ~30 days, grouped by day,
 * with full ops detail (prep_count, staff_needed, prep_notes, ops_notes)
 * so the crew member always knows: what, where, when, how much to make,
 * and any gotchas.
 *
 * Error states surface clearly:
 *   404 → "This invite link isn't valid" (typo / never existed)
 *   410 → "This invite was revoked" (owner pulled access)
 *   500 → "Something went wrong"
 *
 * Phase 1B intentionally NOT in scope:
 *   * Edit affordances (read-only by design)
 *   * Per-staff event assignment filter (everyone sees all staff+public)
 *   * Account upgrade flow ("save this calendar to your phone" deep link)
 *   * Push notifications
 */
import React, { useEffect, useState } from "react";
import { BACKEND_BASE } from "../lib/backendBase";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Calendar, MapPin, Users, Hamburger, ListChecks, Lock, Truck, CheckCircle, XCircle, CaretDown, Crown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fmtET } from "../lib/etTime";

const API = `${BACKEND_BASE}/api`;

// Mirror the backend visibility tiers so the chip text + color match
// every other surface (schedule editor, owner dashboard).
const VIS = {
  public:  { label: "Public",  emoji: "🌐", cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  staff:   { label: "Staff",   emoji: "👥", cls: "bg-indigo-100 text-indigo-700 border-indigo-300" },
  private: { label: "Private", emoji: "🔒", cls: "bg-slate-100 text-slate-700 border-slate-300" },
};

const TYPE_LABEL = {
  street: "Street / Lot",
  festival: "Festival",
  catering: "Catering",
  private_party: "Private Party",
  popup: "Pop-up",
  farmers_market: "Farmers Market",
  brewery: "Brewery / Taproom",
  corporate: "Corporate",
  other: "Other",
};

/** Format an ISO datetime as "Mon, Jun 30 · 11:00 AM – 2:00 PM",
    always in ET (America/New_York) — the truck's timezone — no matter
    where the staff member's device is set. */
function fmtRange(starts, ends) {
  const dayPart = fmtET(starts, { weekday: "short", month: "short", day: "numeric" });
  const sTime = fmtET(starts, { hour: "numeric", minute: "2-digit" });
  const eTime = ends ? fmtET(ends, { hour: "numeric", minute: "2-digit" }) : "";
  return { dayPart, timePart: eTime ? `${sTime} – ${eTime}` : sTime };
}

/** Bucket events by their ET date string so we can render one
    section per day. Keeps the order chronological. */
function groupByDay(events) {
  const buckets = new Map();
  events.forEach((e) => {
    const key = fmtET(e.starts_at, { weekday: "long", month: "long", day: "numeric" });
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(e);
  });
  return Array.from(buckets.entries());
}

export default function StaffView() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/staff/view/${token}`);
        if (!cancelled) {
          setState({ loading: false, error: null, data });
          // Remember this staff link on-device so the navbar Calendar
          // tab can route back here even when the crew member has no
          // TruckTrackr account (or is logged in under a different
          // email than the invite). Cleared below on 404/410.
          try { localStorage.setItem("tt_staff_token", token); } catch { /* private browsing */ }
        }
      } catch (e) {
        if (!cancelled) {
          const status = e?.response?.status;
          const msg =
            status === 410 ? "This invite has been revoked by the owner."
            : status === 404 ? "This invite link isn't valid. Double-check the URL or ask the owner to resend."
            : "Couldn't load the schedule. Try refreshing in a moment.";
          setState({ loading: false, error: msg, data: null });
          // Dead link (revoked or never existed) — forget it so the
          // Calendar tab stops routing here.
          if (status === 404 || status === 410) {
            try {
              if (localStorage.getItem("tt_staff_token") === token) {
                localStorage.removeItem("tt_staff_token");
              }
            } catch { /* private browsing */ }
          }
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Recruiting funnel attribution (Feb 27 2026). When the staff view
  // successfully loads, stash the inviting owner's id + acquisition
  // source in localStorage so a staff member who clicks the
  // "Run your own truck?" CTA below carries attribution through the
  // signup flow even after the page navigates away. The signup
  // handler reads these keys and stamps them onto the new user
  // record (and bumps the inviting owner's referral_count). Hook
  // MUST live above conditional returns per the rules of Hooks.
  const ownerId = state.data?.owner?.id;
  useEffect(() => {
    if (ownerId) {
      try {
        localStorage.setItem("tt_acquisition_source", "staff_invite");
        localStorage.setItem("tt_referrer_owner_id", ownerId);
      } catch { /* private-browsing fallthrough */ }
    }
  }, [ownerId]);

  // Phase 1E — expanded card state. Keyed by event_id. Tapping the
  // card header toggles a detail panel revealing the full crew roster
  // (owner + assigned staff with each member's RSVP) and any ops
  // notes inline. Mobile-first: a sheet/modal would be heavier UX
  // than tap-to-expand for this read-only browsing case.
  const [expandedId, setExpandedId] = useState(null);

  // Optimistic RSVP map keyed by event_id → response. Hook MUST live
  // above conditional returns per the rules of Hooks. Re-seeded
  // whenever the parent data loads from /staff/view.
  const inviteIdFromData = state.data?.invite_id;
  const eventsFromData = state.data?.events;
  const [rsvpsByEvent, setRsvpsByEvent] = useState({});
  useEffect(() => {
    if (!eventsFromData || !inviteIdFromData) return;
    const seed = {};
    eventsFromData.forEach((e) => {
      const mine = e?.rsvps?.[inviteIdFromData];
      if (mine?.response) seed[e.id] = mine.response;
    });
    setRsvpsByEvent(seed);
  }, [eventsFromData, inviteIdFromData]);

  // ── Home-screen fix (Mar 2026) ──────────────────────────────────────
  // The site manifest's start_url is "/?source=pwa", so "Add to Home
  // Screen" from ANY page creates an icon that opens the landing page in
  // a standalone, storage-ISOLATED context (especially iOS) — the crew
  // member's remembered link doesn't exist there, and they bounce to the
  // CrewGate (real support case). While the schedule page is open, swap
  // in a blob manifest whose start_url IS this /staff/:token URL — the
  // token travels in the bookmark itself, no storage needed. Restored on
  // unmount so the rest of the site keeps normal PWA install behavior.
  useEffect(() => {
    if (!token) return undefined;
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return undefined;
    const originalHref = link.getAttribute("href");
    const origin = window.location.origin;
    const manifest = {
      name: "Crew Schedule — TruckTrackr",
      short_name: "Crew Schedule",
      id: `${origin}/staff/${token}`,
      start_url: `${origin}/staff/${token}`,
      scope: `${origin}/`,
      display: "standalone",
      background_color: "#ffffff",
      theme_color: "#ea580c",
      // Absolute icon URLs — relative ones would resolve against the
      // blob URL and 404.
      icons: [
        { src: `${origin}/icon-192.png`, sizes: "192x192", type: "image/png" },
        { src: `${origin}/icon-512.png`, sizes: "512x512", type: "image/png" },
      ],
    };
    const blobUrl = URL.createObjectURL(
      new Blob([JSON.stringify(manifest)], { type: "application/manifest+json" }),
    );
    link.setAttribute("href", blobUrl);
    // iOS also names the bookmark from this meta / the document title.
    const appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    const originalAppleTitle = appleTitle?.getAttribute("content");
    appleTitle?.setAttribute("content", "Crew Schedule");
    const originalDocTitle = document.title;
    document.title = "Crew Schedule — TruckTrackr";
    return () => {
      if (originalHref) link.setAttribute("href", originalHref);
      if (appleTitle && originalAppleTitle) appleTitle.setAttribute("content", originalAppleTitle);
      document.title = originalDocTitle;
      URL.revokeObjectURL(blobUrl);
    };
  }, [token]);

  const submitRsvp = async (eventId, response) => {
    // Optimistic update — flip immediately so the UI feels instant
    // even on a flaky connection. Revert on failure.
    const prior = rsvpsByEvent[eventId];
    setRsvpsByEvent((s) => ({ ...s, [eventId]: response }));
    try {
      await axios.post(`${API}/staff/${token}/events/${eventId}/rsvp`, { response });
      toast.success(response === "going" ? "Confirmed — see you there!" : "Got it — owner notified.");
    } catch (e) {
      setRsvpsByEvent((s) => {
        const next = { ...s };
        if (prior) next[eventId] = prior;
        else delete next[eventId];
        return next;
      });
      const status = e?.response?.status;
      if (status === 403) {
        toast.error("You aren't assigned to this event.");
      } else if (status === 410) {
        toast.error("This invite was revoked.");
      } else {
        toast.error("Couldn't send your RSVP. Try again.");
      }
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground" data-testid="staff-loading">
        Loading schedule…
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md text-center space-y-3" data-testid="staff-error">
          <Lock weight="duotone" className="h-10 w-10 mx-auto text-rose-500" />
          <h1 className="text-xl font-semibold">Can&rsquo;t open this schedule</h1>
          <p className="text-sm text-muted-foreground">{state.error}</p>
        </div>
      </div>
    );
  }

  const { owner, staff_name, invite_id, events = [] } = state.data || {};
  const grouped = groupByDay(events);

  return (
    <div className="min-h-screen bg-background" data-testid="staff-view">
      <header className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary">
              Staff schedule
            </div>
            <div className="font-semibold text-sm truncate">
              {owner?.name || "Truck"} · for {staff_name || "you"}
            </div>
          </div>
          <Link
            to="/"
            data-testid="staff-home-link"
            className="flex-none inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-foreground/80 hover:bg-muted/50 transition-colors"
            title="Go to TruckTrackr home"
          >
            <Truck weight="duotone" className="h-4 w-4 text-primary" />
            TruckTrackr
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {grouped.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border p-8 text-center text-muted-foreground" data-testid="staff-empty">
            <Calendar weight="duotone" className="h-8 w-8 mx-auto mb-2 opacity-60" />
            <p className="text-sm">No upcoming events in the next 30 days.</p>
            <p className="text-xs mt-1">Check back when the owner adds shifts.</p>
          </div>
        )}

        {grouped.map(([day, dayEvents]) => (
          <section key={day} data-testid={`staff-day-${day.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
            <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
              {day}
            </h2>
            <div className="space-y-3">
              {dayEvents.map((e) => {
                const vis = VIS[e.visibility || "public"] || VIS.public;
                const range = fmtRange(e.starts_at, e.ends_at);
                const isExpanded = expandedId === e.id;
                const crew = Array.isArray(e.crew) ? e.crew : [];
                const goingCount = crew.filter((c) => c.rsvp === "going").length;
                const declinedCount = crew.filter((c) => c.rsvp === "declined").length;
                return (
                  <article
                    key={e.id}
                    className={
                      "rounded-2xl border bg-card p-4 space-y-2 " +
                      (e.is_canceled ? "border-rose-300 bg-rose-50/50" : "border-border")
                    }
                    data-testid={`staff-event-${e.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => setExpandedId(isExpanded ? null : e.id)}
                      data-testid={`staff-event-toggle-${e.id}`}
                      className="w-full text-left flex items-start justify-between gap-3 focus:outline-none focus:ring-2 focus:ring-primary/40 rounded-md -m-1 p-1"
                    >
                      <div className="min-w-0 flex-1">
                        <div className={`font-semibold ${e.is_canceled ? "line-through text-rose-700" : ""}`}>
                          {e.event_name || e.truck_name || "Untitled stop"}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">{range.timePart}</div>
                        {crew.length > 0 && (
                          <div className="text-[11px] text-muted-foreground mt-1 inline-flex items-center gap-1.5">
                            <Users weight="duotone" className="h-3 w-3" />
                            <span>{crew.length} crew</span>
                            {goingCount > 0 && (
                              <span className="text-emerald-700">· ✅ {goingCount}</span>
                            )}
                            {declinedCount > 0 && (
                              <span className="text-rose-700">· ❌ {declinedCount}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-none">
                        <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full border px-2 py-0.5 ${vis.cls}`}>
                          {vis.emoji} {vis.label}
                        </span>
                        <CaretDown
                          weight="bold"
                          className={"h-4 w-4 text-muted-foreground/60 transition-transform " + (isExpanded ? "rotate-180" : "")}
                        />
                      </div>
                    </button>
                    {e.is_canceled && (
                      <div className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-1.5 text-xs text-rose-700 font-semibold">
                        🚫 Canceled
                        {e.cancel_reason ? ` · ${e.cancel_reason}` : ""}
                      </div>
                    )}
                    {e.needs_fix && (
                      <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs text-amber-800 font-medium" data-testid={`staff-needs-fix-${e.id}`}>
                        📍 Location TBD — the owner is finalizing where this one will be.
                      </div>
                    )}
                    {e.address && (
                      <div className="text-sm flex items-start gap-1.5 text-foreground/80">
                        <MapPin weight="fill" className="h-4 w-4 mt-0.5 text-primary flex-none" />
                        <span className="leading-snug">{e.address}</span>
                      </div>
                    )}
                    {(e.event_type || e.prep_count != null || e.staff_needed != null) && isExpanded && (
                      <div className="flex flex-wrap gap-2 text-[11px] pt-1">
                        {e.event_type && (
                          <span className="rounded-md bg-muted/60 px-2 py-0.5 font-medium">
                            {TYPE_LABEL[e.event_type] || e.event_type}
                          </span>
                        )}
                        {e.prep_count != null && (
                          <span className="rounded-md bg-amber-100 text-amber-800 px-2 py-0.5 font-medium inline-flex items-center gap-1">
                            <Hamburger weight="fill" className="h-3 w-3" />
                            Prep {e.prep_count}
                          </span>
                        )}
                        {e.staff_needed != null && (
                          <span className="rounded-md bg-indigo-100 text-indigo-800 px-2 py-0.5 font-medium inline-flex items-center gap-1">
                            <Users weight="fill" className="h-3 w-3" />
                            {e.staff_needed} crew
                          </span>
                        )}
                      </div>
                    )}
                    {/* Phase 1E — full crew roster (owner + assigned
                        staff) with per-member RSVP state. Visible only
                        when the card is expanded so the day-glance
                        density stays clean. */}
                    {isExpanded && crew.length > 0 && (
                      <div
                        className="rounded-lg border border-border bg-background px-3 py-2 text-xs space-y-1.5"
                        data-testid={`staff-event-crew-${e.id}`}
                      >
                        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
                          <Users weight="duotone" className="h-3 w-3" /> On this shift
                        </div>
                        {crew.map((c) => (
                          <div key={`${c.kind}-${c.id}`} className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5 min-w-0">
                              {c.kind === "owner" ? (
                                <Crown weight="duotone" className="h-3.5 w-3.5 text-amber-500 flex-none" />
                              ) : (
                                <Users weight="duotone" className="h-3.5 w-3.5 text-primary flex-none" />
                              )}
                              <span className={"truncate " + (c.is_you ? "font-semibold" : "")}>
                                {c.name}
                                {c.is_you && <span className="text-primary"> · you</span>}
                                {c.kind === "owner" && <span className="text-muted-foreground/80"> · owner</span>}
                              </span>
                            </div>
                            {c.kind === "staff" && c.rsvp === "going" && (
                              <span className="text-[10px] font-bold text-emerald-700">✅ Going</span>
                            )}
                            {c.kind === "staff" && c.rsvp === "declined" && (
                              <span className="text-[10px] font-bold text-rose-700">❌ Declined</span>
                            )}
                            {c.kind === "staff" && !c.rsvp && (
                              <span className="text-[10px] text-muted-foreground">⏳ Pending</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {isExpanded && e.prep_notes && (
                      <div className="rounded-lg border border-border bg-background px-3 py-2 text-xs">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1 inline-flex items-center gap-1">
                          <ListChecks weight="duotone" className="h-3 w-3" /> Prep notes
                        </div>
                        <div className="whitespace-pre-wrap leading-snug">{e.prep_notes}</div>
                      </div>
                    )}
                    {isExpanded && e.ops_notes && (
                      <div className="rounded-lg border border-border bg-background px-3 py-2 text-xs">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
                          Ops notes
                        </div>
                        <div className="whitespace-pre-wrap leading-snug">{e.ops_notes}</div>
                      </div>
                    )}
                    {/* RSVP buttons — only when this staff member is
                        actually assigned to this event AND the card is
                        expanded. Otherwise we leave the card collapsed
                        so the day-glance density stays clean. */}
                    {isExpanded && (e.assigned_staff_invite_ids || []).includes(invite_id) && !e.is_canceled && (
                      <div className="pt-1 flex flex-wrap items-center gap-2" data-testid={`staff-rsvp-${e.id}`}>
                        <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                          Your RSVP
                        </span>
                        <button
                          type="button"
                          onClick={() => submitRsvp(e.id, "going")}
                          data-testid={`staff-rsvp-going-${e.id}`}
                          className={
                            "inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold transition-colors " +
                            (rsvpsByEvent[e.id] === "going"
                              ? "bg-emerald-600 text-white"
                              : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200")
                          }
                        >
                          <CheckCircle weight={rsvpsByEvent[e.id] === "going" ? "fill" : "duotone"} className="h-3.5 w-3.5" />
                          I&rsquo;ll be there
                        </button>
                        <button
                          type="button"
                          onClick={() => submitRsvp(e.id, "declined")}
                          data-testid={`staff-rsvp-declined-${e.id}`}
                          className={
                            "inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold transition-colors " +
                            (rsvpsByEvent[e.id] === "declined"
                              ? "bg-rose-600 text-white"
                              : "bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200")
                          }
                        >
                          <XCircle weight={rsvpsByEvent[e.id] === "declined" ? "fill" : "duotone"} className="h-3.5 w-3.5" />
                          Can&rsquo;t make it
                        </button>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
        ))}

        {/* Access helpers (Mar 2026). Two ways crew can always reach the
            schedule: (1) home-screen bookmark — the manifest swap above
            makes the icon open THIS token URL directly, storage-proof;
            (2) a free account with the invited email — invites auto-link
            at login, so the Calendar tab works on every device/PC. */}
        <div
          className="rounded-2xl border border-border bg-muted/20 p-4 space-y-3"
          data-testid="staff-any-device-tip"
        >
          <div className="text-left">
            <div className="text-xs font-bold text-foreground/90">
              📌 Keep this schedule one tap away
            </div>
            <p className="text-xs text-muted-foreground leading-snug mt-1">
              Add it to your home screen <span className="font-semibold">from this page</span>:{" "}
              {/iPhone|iPad|iPod/i.test(navigator.userAgent)
                ? "tap the Share button, then “Add to Home Screen.”"
                : /Android/i.test(navigator.userAgent)
                ? "tap the ⋮ menu, then “Add to Home Screen.”"
                : "in your browser menu choose “Add to Home Screen” (mobile) or bookmark this page (desktop)."}{" "}
              The icon will open your schedule directly.
            </p>
          </div>
          <div className="text-left border-t border-border pt-3">
            <div className="text-xs font-bold text-foreground/90">
              💻 Using a PC or another phone?
            </div>
            <p className="text-xs text-muted-foreground leading-snug mt-1">
              Create a free account with the email your invite was sent to —
              then the Calendar tab finds your schedule on{" "}
              <span className="font-semibold">every device you log into</span>.
            </p>
            <Link
              to="/signup?ref=staff_device"
              data-testid="staff-any-device-signup"
              className="inline-block mt-1.5 text-xs font-semibold text-primary hover:underline"
            >
              Create free account →
            </Link>
          </div>
        </div>
      </main>

      {/* Staff → owner recruiting funnel (Feb 27 2026 / Phase 1B).
          Every staff member viewing this read-only schedule is a
          potential future TruckTrackr owner — they're either looking
          at this app daily and silently wondering "could I run my
          own truck?" or they already have a side hustle and don't
          know we exist. The CTA hands them the on-ramp. The
          `?ref=staff_invite&from={owner_id}` query params get
          captured server-side at signup so the inviting owner gets
          referral credit (drives leaderboards / future bonuses). */}
      <section
        className="border-t border-border bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20"
        data-testid="staff-recruit-cta"
      >
        <div className="max-w-2xl mx-auto px-4 py-8 text-center space-y-3">
          <Truck weight="duotone" className="h-10 w-10 mx-auto text-primary" />
          <h2 className="text-lg font-bold font-display">
            Run your own truck?
          </h2>
          <p className="text-sm text-muted-foreground max-w-md mx-auto leading-snug">
            TruckTrackr puts your schedule on the customer map, syncs Google &amp; Outlook calendars,
            and lets you coordinate your whole crew like {owner?.name || "this truck"} does.
          </p>
          <Link
            to={`/signup?ref=staff_invite&from=${encodeURIComponent(owner?.id || "")}`}
            data-testid="staff-recruit-cta-signup"
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-5 py-2.5 text-sm font-semibold transition-colors"
          >
            Get on TruckTrackr — it&rsquo;s free
          </Link>
          <p className="text-[10px] text-muted-foreground/80 mt-2">
            Free to start · 1 staff seat included · No credit card required
          </p>
        </div>
      </section>

      <footer className="max-w-2xl mx-auto px-4 pb-8 pt-2 text-center text-[10px] text-muted-foreground">
        Read-only staff view · Powered by TruckTrackr
      </footer>
    </div>
  );
}
